import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useStytchMemberSession } from '@stytch/react/b2b';
import { useSessionJwt } from '../../../hooks/useSessionJwt';
import { Sparkles } from 'lucide-react';
import ErrorBoundary from './shared/ErrorBoundary';
import TabNavigation from './shared/TabNavigation';
import SpanTab from './tabs/SpanTab';
import OutlineTab from './tabs/OutlineTab';
import { palette, withOpacity } from '../../../utils/designTokens';
import { logger } from '../../../utils/logger';
import { GRAPH_SELECT_NODE_MESSAGE } from '../../../utils/strings';
import { useEnvironment } from '../../../contexts/EnvironmentContext';
import {
  extractInputOutputMessages,
  extractToolAttributes,
  extractLLMTools,
  extractPromptTemplate,
  extractPrompts,
  extractInvocationParameters
} from '../../../utils/openinference';
import { isInDemoMode } from '../../../services/demoDataService';
import { fetchSpanDetail, fetchSpanMessages, fetchSpanDocuments, fetchSpanToolSchema } from '../../../services/traceDetailApi';
import { getDetectionsForSpan } from '../../../utils/spanDetections';


/**
 * Check if span is a LiveKit span based on name or attributes
 */
const isLiveKitSpan = (span) => {
  const liveKitSpanNames = [
    'agent_session', 'user_turn', 'assistant_turn', 'llm_node', 'llm_request',
    'tts_request', 'tts_node', 'tts_request_run', 'stt_request', 'stt_node',
    'user_speaking', 'agent_speaking', 'eou_detection', 'on_enter', 'on_exit',
    'start_agent_activity', 'drain_agent_activity'
  ];
  const name = span.name || span.span_name || '';
  return liveKitSpanNames.includes(name);
};

/**
 * Check if Input tab has content
 */
const hasInputContent = (span, spanKind) => {
  if (span.input_value) return true;

  // v2: use direct fields
  if (span.__v2Span) {
    return spanKind === 'llm' && !!span.user_query;
  }

  // Check for LLM input messages
  if (spanKind === 'llm') {
    try {
      const { input } = extractInputOutputMessages(span.attributes);
      if (input && input.length > 0) return true;
    } catch (e) {
      // Ignore errors
    }
  }

  // Check for TOOL input
  if (spanKind === 'tool') {
    try {
      const { input } = extractToolAttributes(span.attributes);
      if (input) return true;
    } catch (e) {
      // Ignore errors
    }
  }

  // Check for LiveKit input attributes (nested after API unflatten)
  if (isLiveKitSpan(span)) {
    try {
      const attrs = typeof span.attributes === 'string' ? JSON.parse(span.attributes) : span.attributes || {};
      const lk = attrs.lk || {};
      // LiveKit spans with input content
      if (lk.user_input) return true;  // assistant_turn
      if (lk.input_text) return true;  // tts_request
      if (lk.chat_ctx) return true;    // llm_node, eou_detection
    } catch (e) {
      // Ignore errors
    }
  }

  return false;
};

/**
 * Check if Output tab has content
 */
const hasOutputContent = (span, spanKind) => {
  if (span.output_value) return true;
  if (span.status_code === 'ERROR' || span.metrics?.has_error) return true;

  // v2: use direct fields
  if (span.__v2Span) {
    if (spanKind === 'llm') return !!span.assistant_response;
    if (spanKind === 'retriever') return (span.retriever_document_count ?? 0) > 0;
    return false;
  }

  // Check for LLM output messages
  if (spanKind === 'llm') {
    try {
      const attrs = typeof span.attributes === 'string' ? JSON.parse(span.attributes) : span.attributes;
      const llmAttrs = attrs?.llm || {};
      const outputMessages = llmAttrs.output_messages;

      // Check for raw output_messages directly (handles both content/contents formats)
      if (outputMessages && (Array.isArray(outputMessages) ? outputMessages.length > 0 : Object.keys(outputMessages).length > 0)) {
        return true;
      }
    } catch (e) {
      // Ignore errors
    }
  }

  // Check for TOOL output
  if (spanKind === 'tool') {
    try {
      const { output } = extractToolAttributes(span.attributes);
      if (output) return true;
    } catch (e) {
      // Ignore errors
    }
  }

  // Check for RETRIEVER documents
  if (spanKind === 'retriever') {
    try {
      const attributes = typeof span.attributes === 'string'
        ? JSON.parse(span.attributes)
        : span.attributes || {};
      // Check for nested structure: attributes.retrieval.documents array
      const documents = attributes?.retrieval?.documents;
      if (Array.isArray(documents) && documents.length > 0) return true;
    } catch (e) {
      // Ignore errors
    }
  }

  // Check for LiveKit output attributes (nested after API unflatten)
  if (isLiveKitSpan(span)) {
    try {
      const attrs = typeof span.attributes === 'string' ? JSON.parse(span.attributes) : span.attributes || {};
      const lk = attrs.lk || {};
      // LiveKit spans with output content
      if (lk.response?.text) return true;    // assistant_turn, llm_node
      if (lk.user_transcript) return true;   // user_turn
      if (lk.tts_metrics) return true;       // tts_request
      if (lk.llm_metrics) return true;       // llm_request
    } catch (e) {
      // Ignore errors
    }
  }

  return false;
};

/**
 * Check if Configuration tab has content (LLM only)
 * Now includes system prompt, so LLM spans with input messages always have config content
 */
const hasConfigurationContent = (span) => {
  // v2: use direct fields
  if (span.__v2Span) {
    return !!(span.system_prompt || span.invocation_params || span.prompt_template);
  }

  try {
    const { input: inputMessages } = extractInputOutputMessages(span.attributes);
    if (inputMessages && inputMessages.length > 0) return true;

    const tools = extractLLMTools(span.attributes);
    if (tools && tools.length > 0) return true;

    const promptTemplate = extractPromptTemplate(span.attributes);
    if (promptTemplate) return true;

    const prompts = extractPrompts(span.attributes);
    if (prompts && prompts.length > 0) return true;

    const invocationParams = extractInvocationParameters(span.attributes);
    if (invocationParams && Object.keys(invocationParams).length > 0) return true;
  } catch (e) {
    // Ignore errors
  }

  return false;
};

/**
 * Check if Security tab has content
 */
const hasSecurityContent = (span) => {
  const detections = Array.isArray(span.detections) ? span.detections : [];
  return detections.length > 0;
};

/**
 * Extract playground configuration from an LLM span
 * Returns config suitable for passing to Playground component
 */
// traceId is passed in, not read off the span: v2 span rows carry no trace_id (it is a
// WHERE-clause input, not a projected column). This function is module-level, so the value has
// to be threaded from the component rather than closed over.
const extractPlaygroundConfig = (span, traceId) => {
  if (!span) {
    console.warn('[Playground] extractPlaygroundConfig called with null span');
    return null;
  }

  try {
    // Debug: Log full span structure (visible in browser console)
    console.log('[Playground] Full span object:', JSON.stringify(span, null, 2));

    const attributes = typeof span.attributes === 'string'
      ? JSON.parse(span.attributes)
      : span.attributes || {};

    console.log('[Playground] Parsed attributes:', JSON.stringify(attributes, null, 2));

    // Extract model info - check multiple possible locations
    // Priority: top-level span fields (from API) > nested attributes > flattened attributes
    const llmAttrs = attributes?.llm || {};

    const modelName = span.llm_model_name        // Top-level from span details API
      || llmAttrs.model_name                     // Nested: attributes.llm.model_name
      || attributes?.['llm.model_name']          // Flattened: attributes['llm.model_name']
      || '';

    console.log('[Playground] Model extraction:', {
      'span.llm_model_name': span.llm_model_name,
      'llmAttrs.model_name': llmAttrs.model_name,
      "attributes['llm.model_name']": attributes?.['llm.model_name'],
      'final modelName': modelName
    });

    // Determine provider - check multiple sources
    let provider = span.llm_provider             // Top-level from span details API
      || span.llm_system                         // Alternative top-level field
      || llmAttrs.provider                       // Nested: attributes.llm.provider
      || attributes?.['llm.provider']            // Flattened
      || '';

    console.log('[Playground] Provider extraction:', {
      'span.llm_provider': span.llm_provider,
      'span.llm_system': span.llm_system,
      'llmAttrs.provider': llmAttrs.provider,
      "attributes['llm.provider']": attributes?.['llm.provider'],
      'initial provider': provider
    });

    // Infer provider from model name if not explicitly set
    if (!provider && modelName) {
      const modelLower = modelName.toLowerCase();
      if (modelLower.includes('claude') || modelLower.includes('anthropic')) {
        provider = 'anthropic';
      } else if (modelLower.includes('gemini') || modelLower.includes('google')) {
        provider = 'google';
      } else if (modelLower.includes('gpt') || modelLower.includes('o1') || modelLower.includes('o3')) {
        provider = 'openai';
      } else {
        provider = 'openai'; // Default
      }
      console.log('[Playground] Provider inferred from model name:', provider);
    }

    // Default to openai if still empty
    if (!provider) {
      provider = 'openai';
      console.log('[Playground] Provider defaulting to openai');
    }

    // Extract messages (input)
    const { input: inputMessages } = extractInputOutputMessages(span.attributes);
    console.log('[Playground] Extracted input messages:', inputMessages);

    // Separate system prompt from user/assistant messages
    let systemPrompt = '';
    const messages = [];

    if (Array.isArray(inputMessages)) {
      for (const msg of inputMessages) {
        if (msg.role === 'system') {
          systemPrompt = msg.content || '';
        } else if (msg.role === 'user' || msg.role === 'assistant') {
          messages.push({
            role: msg.role,
            content: msg.content || ''
          });
        }
      }
    }

    console.log('[Playground] System prompt:', systemPrompt ? `${systemPrompt.substring(0, 100)}...` : '(empty)');
    console.log('[Playground] Messages count:', messages.length);

    // If no messages extracted, add empty user message
    if (messages.length === 0) {
      messages.push({ role: 'user', content: '' });
    }

    // Extract invocation parameters
    const invocationParams = extractInvocationParameters(span.attributes) || {};
    console.log('[Playground] Invocation parameters:', invocationParams);

    // Extract tools
    const tools = extractLLMTools(span.attributes) || [];
    console.log('[Playground] Tools count:', tools.length);

    const config = {
      provider,
      model: modelName,
      systemPrompt,
      messages,
      parameters: {
        temperature: invocationParams.temperature ?? 0.7,
        max_tokens: invocationParams.max_tokens ?? 1024,
        top_p: invocationParams.top_p ?? 1.0,
        frequency_penalty: invocationParams.frequency_penalty ?? 0,
      },
      tools,
      sourceSpan: {
        spanId: span.span_id,
        // Threaded from the component's traceId prop (see the signature above). Reading it off
        // the span handed the Playground `traceId: undefined` and lost the provenance link.
        traceId,
        spanName: span.name
      }
    };

    console.log('[Playground] Final config:', {
      provider: config.provider,
      model: config.model,
      systemPromptLength: config.systemPrompt?.length || 0,
      messagesCount: config.messages?.length || 0,
      parameters: config.parameters,
      toolsCount: config.tools?.length || 0
    });

    return config;
  } catch (err) {
    console.error('[Playground] Failed to extract playground config:', err);
    return null;
  }
};

/**
 * SpanDetailsPanel - Tab navigation container for span details
 * Routes to appropriate tab component based on activeTab prop
 *
 * @param {Object} span - Lightweight span object (may not have attributes/input/output)
 * @param {Array} spans - All spans in the trace (for cross-span lookups like sibling prompts)
 * @param {string} activeTab - Active tab ID (controlled by parent)
 * @param {Function} onTabChange - Callback when tab is changed
 * @param {Function} onClose - Optional callback when panel is closed
 * @param {string} traceId - Trace ID for fetching full span details
 */
const SpanDetailsPanel = ({ span, spans = [], traceDetections = [], activeTab, onTabChange, onClose, traceId, selectedApp: selectedAppProp }) => {
  const { selectedAppId: selectedAppFromUrl } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { session } = useStytchMemberSession();
  const { selectedEnvironment } = useEnvironment();
  const { sessionId, getJwt } = useSessionJwt();
  const tenantId = session?.organization_id || null;

  // Cache for full span details (attributes, input, output) and failed span IDs
  const [spanDetailsCache, setSpanDetailsCache] = useState({});
  const [failedSpanIds, setFailedSpanIds] = useState(new Set());
  const [loadingSpanId, setLoadingSpanId] = useState(null);
  const [detailsErrors, setDetailsErrors] = useState({});
  const inFlightDetailSpanIds = useRef(new Set());

  // Clear caches when trace changes to prevent stale span fetches
  useEffect(() => {
    setSpanDetailsCache({});
    setFailedSpanIds(new Set());
    setDetailsErrors({});
    setLoadingSpanId(null);
    inFlightDetailSpanIds.current.clear();
  }, [traceId]);

  // Get app ID from prop, URL params, or from span's service info
  const selectedApp = selectedAppProp || selectedAppFromUrl || span?.service_id || span?.service_name;

  // State for playground loading
  const [loadingPlayground, setLoadingPlayground] = useState(false);

  // Handle opening span in playground (only for LLM spans)
  const handleOpenInPlayground = useCallback(async () => {
    if (!span || !selectedApp) return;

    let spanToUse = spanDetailsCache[span.span_id];

    // If we don't have full details, fetch them first
    if (!spanToUse || spanToUse.__spanDetailView !== 'full') {
      try {
        setLoadingPlayground(true);
        const jwt = getJwt();
        if (!jwt) {
          logger.error('No JWT token available for fetching span details');
          return;
        }

        logger.debug('Fetching full span details for playground:', { spanId: span.span_id, traceId });
        spanToUse = await fetchSpanDetail(traceId, span.span_id, selectedApp, selectedEnvironment, jwt, { view: 'full' });

        // Cache the fetched details
        setSpanDetailsCache(prev => ({
          ...prev,
          [span.span_id]: { ...span, ...spanToUse }
        }));
      } catch (err) {
        logger.error('Failed to fetch span details for playground:', err);
        return;
      } finally {
        setLoadingPlayground(false);
      }
    }

    const playgroundConfig = extractPlaygroundConfig(spanToUse, traceId);

    if (!playgroundConfig) {
      logger.error('Failed to extract playground config from span');
      return;
    }

    // Preserve existing query params (env, range) and set tab to playground
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'playground');

    // Navigate to app detail page with playground tab and config in state
    navigate(`/app/${selectedApp}?${params.toString()}`, {
      state: { playgroundConfig }
    });
  }, [span, spanDetailsCache, selectedApp, navigate, searchParams, getJwt, traceId, selectedEnvironment]);

  if (!span) {
    return (
      <div className="text-sm text-text-muted p-4">
        {GRAPH_SELECT_NODE_MESSAGE}
      </div>
    );
  }

  logger.debug('SpanDetailsPanel rendering:', { spanId: span.span_id, spanKind: span.span_kind });

  const spanKind = (span.span_kind || 'chain').toLowerCase();
  const isLLMSpan = spanKind === 'llm';

  // Check if we have full details for this span (cached, or span already has attributes from bulk fetch).
  // v2 list spans carry attributes:{} as a no-op placeholder but are NOT fully loaded — only a span
  // fetched via the detail endpoint sets __hasFullSpanDetail:true.
  const spanHasDetails =
    span.__hasFullSpanDetail === true ||
    (!span.__v2Span && (span.attributes !== undefined || span.input_value !== undefined));
  const cachedSpanDetails = spanDetailsCache[span.span_id];
  const hasCachedDetails = cachedSpanDetails !== undefined;
  const hasDetailsForDetailsTab = hasCachedDetails || spanHasDetails;
  const fullSpan = cachedSpanDetails || span;
  const displayDetections = useMemo(
    () => getDetectionsForSpan(fullSpan, traceDetections),
    [fullSpan, traceDetections]
  );
  const displaySpan = useMemo(
    () => ({ ...fullSpan, detections: displayDetections }),
    [fullSpan, displayDetections]
  );
  const detailsError = detailsErrors[span.span_id] || null;

  // Drilldown API: bound fetch functions for banned-column endpoints.
  // Available as soon as a v2 span-list row exists; do not wait for the heavier
  // span-detail endpoint before loading outline messages/documents.
  const drilldownApi = useMemo(() => {
    if (!span.__v2Span || !getJwt() || !selectedApp) return null;
    const spanId = span.span_id;
    return {
      fetchMessages:   (options = {}) => fetchSpanMessages(traceId, spanId, selectedApp, selectedEnvironment, getJwt(), options),
      fetchDocuments:  (options = {}) => fetchSpanDocuments(traceId, spanId, selectedApp, selectedEnvironment, getJwt(), options),
      fetchToolSchema: (options = {}) => fetchSpanToolSchema(traceId, spanId, selectedApp, selectedEnvironment, getJwt(), options),
    };
  }, [span.span_id, span.__v2Span, traceId, selectedApp, selectedEnvironment, sessionId, getJwt]);

  // Drilldown messages shared between InputTab (context) and OutputTab (output messages).
  // Fetched eagerly when a v2 LLM span is selected so output loads immediately.
  const [drilldownMessages, setDrilldownMessages] = useState(null);
  const [drilldownMessagesLoading, setDrilldownMessagesLoading] = useState(false);
  const [drilldownMessagesError, setDrilldownMessagesError] = useState(null);
  const drilldownFetchRef = useRef({ inFlight: false, done: false });
  const drilldownAbortRef = useRef(null);

  // Idempotent fetch — safe to call from multiple places; fires only once per span
  const fetchDrilldownMessages = useCallback(() => {
    const ref = drilldownFetchRef.current;
    if (!drilldownApi || spanKind !== 'llm' || ref.inFlight || ref.done) return;
    ref.inFlight = true;
    setDrilldownMessagesLoading(true);
    const controller = new AbortController();
    drilldownAbortRef.current = controller;
    drilldownApi.fetchMessages({ signal: controller.signal })
      .then(data => {
        ref.inFlight = false;
        ref.done = true;
        setDrilldownMessages(data);
        setDrilldownMessagesLoading(false);
      })
      .catch(err => {
        ref.inFlight = false;
        if (err?.name === 'AbortError') return;
        setDrilldownMessagesLoading(false);
        setDrilldownMessagesError(err?.message || 'Failed to load messages');
      });
  }, [drilldownApi, spanKind]);

  // Eager fetch: triggers when span changes so output messages load without user interaction
  useEffect(() => {
    drilldownAbortRef.current?.abort();
    setDrilldownMessages(null);
    setDrilldownMessagesLoading(false);
    setDrilldownMessagesError(null);
    drilldownFetchRef.current = { inFlight: false, done: false };
    fetchDrilldownMessages();
  }, [span.span_id]); // eslint-disable-line

  // Lazy load full details only for the Details tab. Outline can render from the
  // list-preview row plus dedicated drilldown endpoints.
  const needsFullDetails = activeTab === 'span';

  useEffect(() => {
    // Only fetch if we need full details, don't have them, and haven't already failed for this span
    if (
      !needsFullDetails ||
      hasDetailsForDetailsTab ||
      inFlightDetailSpanIds.current.has(span.span_id) ||
      failedSpanIds.has(span.span_id)
    ) {
      return;
    }

    // Check if we're in demo mode - if so, use span data directly (already has attributes)
    if (isInDemoMode(tenantId, selectedEnvironment, selectedApp)) {
      // In demo mode, the span already has all the data we need (attributes, input_value, output_value)
      // Cache it directly without making an API call
      setSpanDetailsCache(prev => ({
        ...prev,
        [span.span_id]: span
      }));
      logger.debug('Using demo span data directly:', { spanId: span.span_id });
      return;
    }

    const spanId = span.span_id;
    let cancelled = false;
    const controller = new AbortController();

    const loadDetails = async () => {
      try {
        inFlightDetailSpanIds.current.add(spanId);
        setLoadingSpanId(spanId);
        setDetailsErrors(prev => {
          if (!prev[spanId]) return prev;
          const next = { ...prev };
          delete next[spanId];
          return next;
        });

        const jwt = getJwt();
        if (!jwt) {
          throw new Error('No JWT token available');
        }

        logger.debug('Fetching full span details:', { spanId, traceId });
        const fullSpanData = await fetchSpanDetail(traceId, spanId, selectedApp, selectedEnvironment, jwt, { signal: controller.signal, view: 'metadata' });
        if (cancelled) return;

        // Cache the full span details
        setSpanDetailsCache(prev => ({
          ...prev,
          [spanId]: { ...span, ...fullSpanData }
        }));

        logger.debug('Loaded full span details:', { spanId });
      } catch (err) {
        if (cancelled) return;
        if (err?.name === 'AbortError') return;
        logger.error('Failed to fetch span details:', err);
        setDetailsErrors(prev => ({
          ...prev,
          [spanId]: err.message
        }));
        // Mark this span as failed so we don't retry in a loop
        setFailedSpanIds(prev => new Set(prev).add(spanId));
      } finally {
        inFlightDetailSpanIds.current.delete(spanId);
        if (!cancelled) {
          setLoadingSpanId(prev => (prev === spanId ? null : prev));
        }
      }
    };

    loadDetails();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [span.span_id, traceId, needsFullDetails, hasDetailsForDetailsTab, failedSpanIds, tenantId, selectedEnvironment, selectedApp, sessionId, getJwt]);

  // Build tabs array dynamically based on content availability
  const tabs = [
    { id: 'span', label: 'Details' } // Details tab always shown
  ];

  // Determine content availability for the Outline tab
  let outlineHasInput = false;
  let outlineHasOutput = false;
  let outlineHasConfig = false;

  if (!span.__v2Span && spanHasDetails) {
    outlineHasInput = hasInputContent(displaySpan, spanKind);
    outlineHasOutput = hasOutputContent(displaySpan, spanKind);
    outlineHasConfig = isLLMSpan && hasConfigurationContent(displaySpan);
  } else {
    const inputOutputSpans = ['llm', 'tool', 'chain', 'agent'];
    const outputOnlySpans = ['retriever'];
    const liveKitInputSpans = ['assistant_turn', 'llm_node', 'tts_request', 'eou_detection'];
    const liveKitOutputSpans = ['user_turn', 'assistant_turn', 'llm_node', 'llm_request', 'tts_request', 'eou_detection'];
    const spanName = span.name || span.span_name || '';

    if (inputOutputSpans.includes(spanKind)) {
      outlineHasInput = true;
      outlineHasOutput = true;
      outlineHasConfig = isLLMSpan;
    } else if (outputOnlySpans.includes(spanKind)) {
      outlineHasOutput = true;
    } else if (isLiveKitSpan(span)) {
      outlineHasInput = liveKitInputSpans.includes(spanName);
      outlineHasOutput = liveKitOutputSpans.includes(spanName);
    }
  }

  const showSecurity = hasSecurityContent(displaySpan);

  // Outline tab - combined view of Input + Output + Configuration + Security
  if (outlineHasInput || outlineHasOutput || outlineHasConfig || showSecurity) {
    tabs.unshift({ id: 'outline', label: 'Outline' });
  }

  // Reset to 'span' tab if current activeTab doesn't exist for this span
  useEffect(() => {
    const tabExists = tabs.some(tab => tab.id === activeTab);
    if (!tabExists) {
      onTabChange(tabs.some(t => t.id === 'outline') ? 'outline' : 'span');
    }
  }, [tabs, activeTab, onTabChange]);

  return (
    <ErrorBoundary>
      <div className="flex flex-col h-full">
        {/* Panel Header */}
        <div className="mb-3 flex-shrink-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-semibold text-text-heading truncate flex-1 pr-2">
              {span.name || 'Unnamed Span'}
            </h3>
            <div className="flex items-center gap-2">
              <div
                className="text-xs uppercase font-semibold px-2 py-0.5 rounded"
                style={{
                  backgroundColor: withOpacity(palette.accents.primary.base, 13),
                  color: palette.accents.primary.base
                }}
              >
                {spanKind}
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex-shrink-0">
          <TabNavigation
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={onTabChange}
          />
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-auto">
          <ErrorBoundary>
            {activeTab === 'span' && (
              needsFullDetails && !hasDetailsForDetailsTab ? (
                <div className="text-sm text-text-muted p-4">Loading span details...</div>
              ) : detailsError ? (
                <div className="text-sm text-danger p-4">Error: {detailsError}</div>
              ) : (
                <SpanTab span={displaySpan} spanKind={spanKind} drilldownApi={drilldownApi} />
              )
            )}
            {activeTab === 'outline' && (
              <OutlineTab
                span={displaySpan}
                spans={spans}
                spanKind={spanKind}
                options={{
                  hasInput: outlineHasInput,
                  hasOutput: outlineHasOutput,
                  hasConfiguration: outlineHasConfig,
                  hasSecurity: showSecurity,
                }}
                drilldownApi={drilldownApi}
                drilldownMessages={drilldownMessages}
                drilldownMessagesLoading={drilldownMessagesLoading}
                drilldownMessagesError={drilldownMessagesError}
                fetchDrilldownMessages={fetchDrilldownMessages}
              />
            )}
          </ErrorBoundary>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default SpanDetailsPanel;
